import { useEffect, useState } from 'react';
import { adminApi } from './api.js';
import { personName } from './support-api.js';

/**
 * A person as the Support screens show them: name and avatar, read from auth.
 *
 * Support stores auth user ids and no people, so both are read at render time
 * through the admin API (`adminApi.user`), once per id per page load: a queue
 * of fifty tickets assigned to three people asks three times. While it loads,
 * or if the lookup fails (an account since closed, a role that cannot list
 * users), the caller shows the short id rather than a blank.
 */
export interface Person {
  name: string | null;
  image: string | null;
}

const people = new Map<string, Promise<Person | null>>();

export function usePerson(userId: string | null): Person | null {
  const [person, setPerson] = useState<Person | null>(null);
  useEffect(() => {
    if (!userId) return;
    let live = true;
    let pending = people.get(userId);
    if (!pending) {
      pending = adminApi
        .user(userId)
        .then((u) => ({ name: personName(u), image: u.image ?? null }))
        .catch(() => null);
      people.set(userId, pending);
    }
    void pending.then((p) => {
      if (live) setPerson(p);
    });
    return () => {
      live = false;
    };
  }, [userId]);
  return person;
}

/** Just the name. Jon, 2026-09-27: an assignee shown as an id was undecipherable. */
export function usePersonName(userId: string | null): string | null {
  return usePerson(userId)?.name ?? null;
}
